<template>
  <EditorPopover
    ref="popover"
    :actionButtons="actionButtons"
    @cancel="cancelEditing"
    @action="onApply">

    <TabbedBox v-if="displayInputField">
      <template v-slot:tablist>
        <Tab :first="true" tabid="tab-edit">
          <i class="far fa-edit"></i> {{title}}
        </Tab>
      </template>

      <template v-slot:tabcontent>
        <TabPage :first="true" tabid="tab-edit">
          <div class="row">
            <label class="col-form-label col-3 font-weight-bold">{{title}}</label>
            <div class="col-9">
              <FormInputText ref="numberInput" type="number" v-model="entryValue" :addon="addon" @focus="onInputFocused" @enter="onInputEnter" />
            </div>
          </div>
          <div class="entry-error-area" v-if="displayInputField">
            <EditorPopoverNotification :text="errorText" />
          </div>

          <div class="row" v-if="displayInputField">
            <template v-if="displayUserFixedField">
              <label class="col-form-label col-2 font-weight-bold">Locked</label>
              <div class="col-3">
                <FormSwitch booleanMode v-model="isUserFixed" @update="lockedStatusUpdated($event)" />
              </div>
            </template>
            <template v-if="displaySegmentMoveOptions">
              <label class="col-form-label col-2 font-weight-bold">Move Type</label>
              <div class="col-6">
                <EditorPopoverOptionButton :active="segmentMoveType == MoveSegmentTypes.move" :icon="iconForMoveSegmentType(MoveSegmentTypes.move)" title="Move" @click="segmentMoveType = MoveSegmentTypes.move"></EditorPopoverOptionButton>
                <EditorPopoverOptionButton :active="segmentMoveType == MoveSegmentTypes.extend" :icon="iconForMoveSegmentType(MoveSegmentTypes.extend)" title="Extend" @click="segmentMoveType = MoveSegmentTypes.extend"></EditorPopoverOptionButton>
                <EditorPopoverOptionButton :active="segmentMoveType == MoveSegmentTypes.taper" :icon="iconForMoveSegmentType(MoveSegmentTypes.taper)" title="Taper" @click="segmentMoveType = MoveSegmentTypes.taper"></EditorPopoverOptionButton>
              </div>
            </template>
            <template v-if="displayDimensionAngleUpdate">
              <label class="col-form-label col-3 font-weight-bold">Angle</label>
              <div class="col-5">
                <FormInputText ref="dimAngleInput" type="number" v-model="dimensionAngle" addon="°" />
              </div>
            </template>

            <div class="col-7" v-if="displayExtendDirection">
              <div class="row">
                <div class="col-7 text-right">
                  <label class="col-form-label font-weight-bold text-right">Edit Direction</label>
                </div>
                <div class="col-5 text-right">
                  <EditorPopoverOptionButton :active="extendDirectionIndex == 0" icon="fa-arrow-from-right" :iconStyle="extendLeftIconStyle" title="Start" @click="extendDirectionIndex = 0" :disabled="extendDirectionDisabled"></EditorPopoverOptionButton>
                  <EditorPopoverOptionButton :active="extendDirectionIndex == 1" icon="fa-arrow-from-right" :iconStyle="extendRightIconStyle" title="End" @click="extendDirectionIndex = 1" :disabled="extendDirectionDisabled"></EditorPopoverOptionButton>
                </div>
              </div>
            </div>
          </div>

          <EditorPopoverButtonList v-if="displayInputField">
            <EditorPopoverButton title="Cancel" type="cancel" icon="fa-times" @click="cancelEditing" />
            <EditorPopoverButton title="Change Datum" type="action" icon="fa-dot-circle" @click="popoverActionButtonClick('changeDatum')" v-if="displayDimensionSourceUpdate" />
            <EditorPopoverButton title="Apply" type="update" icon="fa-check" @click="onApply" />
          </EditorPopoverButtonList>
        </TabPage>
      </template>
    </TabbedBox>

    <EditorKeypad
      v-if="!displayInputField"
      v-model="entryValue"
      :locked="isUserFixed"
      @updateLock="lockedStatusUpdated($event)"
      :title="title"
      :errorText="errorText"
      :showPlusMinus="allowPlusMinus"
      :showExtendDirection="displayExtendDirection"
      :showLock="displayUserFixedField"
      :leftDirectionAngle="extendLeftAngle"
      :rightDirectionAngle="extendRightAngle"
      :extendDirectionIndex="extendDirectionIndex"
      :extendDirectionDisabled="extendDirectionDisabled"
      @updateDirection="directionUpdated($event)">
    </EditorKeypad>
  </EditorPopover>
</template>

<style scoped>
.entry-error-area {
  height: 1rem;
  min-height: 1rem;
}
</style>

<script>
import _ from 'lodash'
import EditorPopoverMixin from '@glass-designer/mixins/editorPopover'
import EditorPopover from './editorPopover'
import EditorKeypad from './editorKeypad'
import EditorTypes from '../types'
import MathsUtils from '@/utils/mathsUtils'

import EditorPopoverButtonList from '@glass-designer/components/popover/editorPopoverButtonList'
import EditorPopoverButton from '@glass-designer/components/popover/editorPopoverButton'
import EditorPopoverNotification from '@glass-designer/components/editorPopoverNotification'
import EditorPopoverOptionButton from '@glass-designer/components/popover/editorPopoverOptionButton'

import { MoveSegmentTypes, MoveSegmentTypeLookup } from '@glass-designer/types'

export default {
  name: 'EditorNumberEntryPopover',
  mixins: [EditorPopoverMixin],
  components: {
    EditorPopover,
    EditorKeypad,
    EditorPopoverButtonList,
    EditorPopoverButton,
    EditorPopoverNotification,
    EditorPopoverOptionButton
  },
  data() {
    return {
      displayInputField: true,
      displayUserFixedField: true,
      displayExtendDirection: false,
      displayDimensionSourceUpdate: false,
      displayDimensionAngleUpdate: false,
      displaySegmentMoveOptions: false,
      allowPlusMinus: true,

      title: 'Entry',
      addon: 'mm',
      errorText: '',
      updateCallback: null,
      actionCallback: null,
      applyCallback: null,
      cancelCallback: null,

      originalValue: 0,
      entryValue: 0,
      dimensionAngle: 0,
      precision: 0,
      extendDirectionIndex: 1,
      extendDirectionAngle: 0,
      isUserFixed: false,
      updateUserFixed: false,
      segmentMoveType: MoveSegmentTypes.move,
    }
  },
  computed: {
    extendLeftAngle() {
      let isFlipped = this.changeValue < 0
      return MathsUtils.roundNumber(MathsUtils.wrapAngleDegrees(this.extendDirectionAngle + (isFlipped ? 180 : 0), 0), this.precision)
    },
    extendRightAngle() {
      let isFlipped = this.changeValue < 0
      return MathsUtils.roundNumber(MathsUtils.wrapAngleDegrees(this.extendDirectionAngle + 180 + (isFlipped ? 180 : 0), 0), this.precision)
    },
    extendLeftIconStyle() {
      let angleDeg = this.extendLeftAngle
      return {
        'transform': `rotate(${angleDeg}deg)`
      }
    },
    extendRightIconStyle() {
      let angleDeg = this.extendRightAngle
      return {
        'transform': `rotate(${angleDeg}deg)`
      }
    },
    extendDirectionDisabled() {
      // If no change in the dimension value, disable the direction
      return Math.abs(this.changeValue) < 0.01
    },
    changeValue() {
      return MathsUtils.roundNumber(this.entryValue, this.precision) - MathsUtils.roundNumber(this.originalValue, this.precision)
    },
    actionButtons() {
      return [
        { icon: 'fa-check', title: 'Apply', tag: 'apply' }
      ]
    }
  },
  watch: {
    entryValue: function() {
      this.debouncedHighlightUpdate()
    },
    dimensionAngle: function() {
      this.debouncedHighlightUpdate()
    },
    extendDirectionIndex: function() {
      this.debouncedHighlightUpdate()
    },
    segmentMoveType: function() {
      this.debouncedHighlightUpdate()
    }
  },
  created() {
    this.MoveSegmentTypes = MoveSegmentTypes
    this.MoveSegmentTypeLookup = MoveSegmentTypeLookup
    this.debouncedHighlightUpdate = _.debounce(this._updateHighlight, 100)
  },
  methods: {
    setup(editor, options) {
      this.clearOptions()

      options = _.defaults(options, {
        value: 0,
        displayUserFixedField: true,
        displayExtendDirection: false,
        displayDimensionSourceUpdate: false,
        displayDimensionAngleUpdate: false,
        displaySegmentMoveOptions: false,
        dimensionAngle: 0,
        extendDirectionAngle: 0,
        precision: 0,
        allowPlusMinus: true,
        title: 'Entry',
        addon: 'mm',
        isUserFixed: false,
        segmentMoveType: MoveSegmentTypes.move
      })

      this.displayInputField = !editor.settings.isTouchDevice
      this.entryValue = 0
      this.extendDirectionIndex = 1
      this.precision = options.precision || 0
      this.originalValue = MathsUtils.roundNumber(options.value, this.precision)
      this.dimensionAngle = MathsUtils.roundNumber(options.dimensionAngle || 0, this.precision)

      // Only set absolute value if input field is used
      if(this.displayInputField)
        this.entryValue = MathsUtils.roundNumber(options.value, this.precision)

      this.isUserFixed = !!options.isUserFixed
      this.segmentMoveType = options.segmentMoveType

      this.displayUserFixedField = !!options.displayUserFixedField
      this.displayExtendDirection = !!options.displayExtendDirection
      this.displayDimensionSourceUpdate = !!options.displayDimensionSourceUpdate
      this.displayDimensionAngleUpdate = !!options.displayDimensionAngleUpdate
      this.displaySegmentMoveOptions = !!options.displaySegmentMoveOptions
      this.extendDirectionAngle = MathsUtils.roundNumber(parseFloat(options.extendDirectionAngle), this.precision)
      this.allowPlusMinus = options.allowPlusMinus

      this.title = options.title
      this.addon = options.addon

      this.errorText = ''
      this.updateUserFixed = false

      // Setup callbacks next tick so they don't get called when resetting values
      this.callbackContext = options.context
      this.updateCallback = options.updateCallback
      this.actionCallback = options.actionCallback
      this.applyCallback = options.applyCallback
      this.cancelCallback = options.cancelCallback
    },
    clearOptions() {
      this.callbackContext = null
      this.updateCallback = null
      this.actionCallback = null
      this.applyCallback = null
      this.cancelCallback = null
      this.entryValue = 0
    },
    displayFromWorldPosition(containerEl, editor, worldPosition, displayPosition) {
      //this.$refs.popover.displayFromWorldPosition(containerEl, editor, worldPosition)   // DEPRECATED. MAY be required for tablet
      this.$refs.popover.displayToolPopover(containerEl, editor, displayPosition)

      // Focus input on display
      // HACK: This has issues working, so we put a slight timeout to ensure the input box is active
      this.$nextTick(() => {
        if(this.displayInputField && this.$refs.numberInput) {
          let inputComponent = this.$refs.numberInput
          setTimeout(() => {
            inputComponent.select()
            inputComponent.focus()
          }, 100)
        }
      })
    },
    updateEntryValue(value) {
      this.entryValue = value
    },
    viewportUpdated(containerEl, editor) {
      //this.$refs.popover.viewportUpdated(containerEl, editor)
    },
    hide() {
      if(this.displayInputField && this.$refs.numberInput)
        this.$refs.numberInput.blur()

      if(this.cancelCallback) {
        // Reset cancel CB so it only is called once when hiding popovers
        let cancelCB = this.cancelCallback
        this.cancelCallback = null

        cancelCB(this.callbackContext)
      }

      this.clearOptions()
      this.$refs.popover.hide()
    },
    onInputFocused() {
      if(this.displayInputField && this.$refs.numberInput)
        this.$refs.numberInput.select()
    },
    onInputEnter(ev) {
      ev.preventDefault()
      this.onApply()
    },
    lockedStatusUpdated(locked) {
      this.isUserFixed = locked
      this.updateUserFixed = true
    },
    onLockUserFixed() {
      this.isUserFixed = true
      this.updateUserFixed = true
    },
    onUnlockUserFixed() {
      this.isUserFixed = false
      this.updateUserFixed = true
    },
    directionUpdated(directionIndex) {
      this.extendDirectionIndex = directionIndex
    },
    iconForMoveSegmentType(moveType) {
      switch(moveType) {
        case MoveSegmentTypes.move:
          return 'fa-arrows'
        case MoveSegmentTypes.extend:
          return 'fa-arrow-from-left'
        case MoveSegmentTypes.taper:
          return 'fa-ruler-triangle'
        default:
          return 'fa-arrows'
      }
    },
    cancelEditing() {
      if(this.cancelCallback)
        this.cancelCallback(this.callbackContext, true)

      this.clearOptions()
      this.hide()
    },
    onApply() {
      this.updateTool({ apply: true })
    },
    _updateHighlight() {
      this.updateTool({ apply: false })
    },
    updateTool(options = {}) {
      this.errorText = ''

      var value = MathsUtils.roundNumber(parseFloat(this.entryValue), this.precision)
      var dimensionAngle = MathsUtils.roundNumber(parseFloat(this.dimensionAngle), this.precision)
      if(isNaN(value)) {
        this.errorText = 'Invalid value'
        return
      }
      if(this.displayDimensionAngleUpdate && isNaN(dimensionAngle)) {
        this.errorText = 'Invalid angle'
        return
      }

      // Apply details to the tool
      // The tool will throw an error or update error text if the action failed
      try {

        if(options.apply) {
          if(this.applyCallback) {
            this.applyCallback(this.callbackContext, {
              value: value,
              dimensionAngle: dimensionAngle,
              isUserFixed: this.isUserFixed,
              updateUserFixed: this.updateUserFixed,
              usingInputField: this.displayInputField,
              extendDirectionIndex: this.extendDirectionIndex,
              segmentMoveType: this.segmentMoveType
            })

            if(this.displayInputField && this.$refs.numberInput)
              this.$refs.numberInput.blur()
            this.hide()
          }
        } else {
          if(this.updateCallback) {
            this.updateCallback(this.callbackContext, {
              value: value,
              dimensionAngle: dimensionAngle,
              isUserFixed: this.isUserFixed,
              updateUserFixed: this.updateUserFixed,
              usingInputField: this.displayInputField,
              extendDirectionIndex: this.extendDirectionIndex,
              segmentMoveType: this.segmentMoveType
            })
          }
        }

      } catch(err) {
        console.error(err)
        this.errorText = `${err.message}`
      }
    },
    setErrorMessage(message) {
      this.errorText = message
    }
  }
}
</script>